import Box from '@mui/material/Box';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { memo } from 'react';
import { Control, useWatch } from 'react-hook-form';

import { AddOnCharge } from '~hooks/useAddOns/models';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { formatPercentage, numberToIntegerOrDecimal } from '~utils/utilFunctions';

import { EM_DASH } from '../SettlementDetails/constants';
import {
  formatAccountingAmount,
  getAddOnRateTypeLabel,
  SettlementSheetAmountInput,
  SettlementSheetCell,
  SettlementSheetRemoveButton,
  settlementSheetRowSx,
} from './settlementSheetDisplay';
import { SettlementSheetFormValues } from './settlementSheetForm';
import { isPercentageAddOnCharge } from './settlementSheetLines';

const getAddOnChargeRateText = (
  addOnCharge: AddOnCharge,
  currencyFormatter: (value: number) => string,
) => {
  if (isPercentageAddOnCharge(addOnCharge)) {
    return formatPercentage(addOnCharge.percentage);
  }

  return isNil(addOnCharge.rate)
    ? EM_DASH
    : formatAccountingAmount(addOnCharge.rate, currencyFormatter);
};

interface SettlementSheetAddOnChargeRowProps {
  addOnCharge: AddOnCharge;
  control: Control<SettlementSheetFormValues>;
  isEditing: boolean;
  onRemove: ((addOnCharge: AddOnCharge) => void) | null;
  totalAmount: number;
}

function SettlementSheetAddOnChargeRow({
  addOnCharge,
  control,
  isEditing,
  onRemove,
  totalAmount,
}: SettlementSheetAddOnChargeRowProps) {
  const { currencyFormatter } = useCompanyCurrency();
  const draftName = `addOnCharges.${addOnCharge.id ?? ''}` as const;
  const draft = useWatch({ control, name: draftName });
  const isEditable = isEditing && draft !== undefined;
  const isPercentage = isPercentageAddOnCharge(addOnCharge);
  const showsQuantity = !isPercentage && addOnCharge.quantity !== 0;

  return (
    <Box data-test-id="settlement-sheet-add-on-charge" sx={settlementSheetRowSx}>
      <SettlementSheetCell column="date" spansUntil="rateType">
        {t('approvals.driver_pay.settlements.add_on_name_prefixed', {
          name: addOnCharge.name,
        })}
      </SettlementSheetCell>
      <SettlementSheetCell column="rateType">
        {getAddOnRateTypeLabel(addOnCharge.revenueCostRateType)}
      </SettlementSheetCell>
      {isEditable && !isPercentage && (
        <SettlementSheetAmountInput
          column="billingQuantity"
          control={control}
          label={t('approvals.driver_pay.settlements.pdf.billing_qty')}
          name={`${draftName}.quantity`}
        />
      )}
      {!isEditable && showsQuantity && (
        <SettlementSheetCell column="billingQuantity">
          {numberToIntegerOrDecimal(addOnCharge.quantity)}
        </SettlementSheetCell>
      )}
      {isEditable ? (
        <SettlementSheetAmountInput
          adornment={isPercentage ? 'percentage' : 'currency'}
          column="rate"
          control={control}
          label={t('approvals.driver_pay.tabs.driver_pay.rate')}
          name={`${draftName}.rate`}
        />
      ) : (
        <SettlementSheetCell column="rate">
          {getAddOnChargeRateText(addOnCharge, currencyFormatter)}
        </SettlementSheetCell>
      )}
      <SettlementSheetCell column="total" isRightAligned>
        {formatAccountingAmount(totalAmount, currencyFormatter)}
      </SettlementSheetCell>
      {onRemove && (
        <SettlementSheetRemoveButton
          label={t('approvals.driver_pay.settlements.lines.remove')}
          onClick={() => onRemove(addOnCharge)}
        />
      )}
    </Box>
  );
}

export default memo(SettlementSheetAddOnChargeRow);
